<script setup lang="ts">
/**
 * Consultant profile card — Booking Matcher (CBM port Phase 3c).
 *
 * Rendered inside `SourcePreviewPane.vue` when the pinned citation
 * points at a profile doc (source startswith "profile:"). Replaces
 * the generic `<pre>` chunk render with a structured view —
 * headline, area, parsed skill table, parsed work history.
 *
 * Profile docs are markdown-shaped (the kompetence_matrix / cv_files
 * connectors render them that way at index time), so this component
 * parses the markdown body client-side rather than asking the backend
 * to return structured JSON — keeps the citation-preview RPC
 * backwards-compatible with non-profile chunks.
 */
import { computed } from "vue";

import type { ChunkContextChunk } from "../api/conversations";

const props = defineProps<{ chunk: ChunkContextChunk }>();

/** Extract `**Area:** Cloud` style lines as a flat key/value list. */
const headerFields = computed(() => {
  const out: Array<{ key: string; value: string }> = [];
  const lines = props.chunk.content.split("\n");
  for (const line of lines) {
    const m = line.match(/^\s*\*\*([^:]+):\*\*\s*(.+?)\s*$/);
    if (m) out.push({ key: m[1].trim(), value: m[2].trim() });
  }
  return out;
});

/** Pull the first markdown table (the `## Kompetencer` table the
 * kompetence_matrix connector renders). Returns `null` when no table
 * is present — falls back to the raw chunk render. */
interface SkillRow { name: string; level: string }
const skills = computed<SkillRow[] | null>(() => {
  const body = props.chunk.content;
  // Find the first `|` row that introduces a table.
  const tableStart = body.indexOf("|");
  if (tableStart < 0) return null;
  const rest = body.slice(tableStart).split("\n");
  const rows: SkillRow[] = [];
  for (const raw of rest) {
    const line = raw.trim();
    if (!line.startsWith("|")) break;
    if (line.startsWith("|---") || line.startsWith("| ---")) continue;
    const cells = line
      .split("|")
      .slice(1, -1)
      .map((c) => c.trim());
    if (cells.length < 2) continue;
    // Skip the header row (case-insensitive match on "kompetence" / "skill").
    if (/(skill|kompetence|name)/i.test(cells[0]) && /(level|niveau)/i.test(cells[1])) continue;
    if (!cells[0]) continue;
    rows.push({ name: cells[0], level: cells[1] });
  }
  return rows.length > 0 ? rows : null;
});

/** Strip the markdown markup (H1, **bold** fields, the skills table)
 * so the remaining "free text" can render as a paragraph below the
 * structured blocks. */
const narrative = computed(() => {
  return props.chunk.content
    .replace(/^#\s+.*$/m, "")                 // drop H1
    .replace(/^\*\*[^:]+:\*\*\s*.+$/gm, "")  // drop **Key:** value
    .replace(/^##\s+Kompetencer[\s\S]*$/m, "") // drop the skill table block
    .replace(/^##\s+.*$/m, "")               // drop other H2
    .trim();
});

const consultantName = computed(() => props.chunk.title || "(unnamed)");
const documentId = computed(() => {
  const src = props.chunk.source;
  return src.startsWith("profile:") ? src.slice("profile:".length) : src;
});
</script>

<template>
  <div class="profile-card">
    <header class="profile-card__head">
      <div class="profile-card__name">{{ consultantName }}</div>
      <div class="profile-card__id">{{ documentId }}</div>
    </header>

    <dl v-if="headerFields.length" class="profile-card__fields">
      <template v-for="f in headerFields" :key="f.key">
        <dt>{{ f.key }}</dt>
        <dd>{{ f.value }}</dd>
      </template>
    </dl>

    <section v-if="skills" class="profile-card__skills">
      <h4>Kompetencer</h4>
      <ul>
        <li v-for="s in skills" :key="s.name">
          <span class="profile-card__skill-name">{{ s.name }}</span>
          <span class="profile-card__skill-level">{{ s.level }}</span>
        </li>
      </ul>
    </section>

    <section v-if="narrative" class="profile-card__narrative">
      <pre class="profile-card__pre">{{ narrative }}</pre>
    </section>
  </div>
</template>

<style scoped>
.profile-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.profile-card__head {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: var(--space-2) var(--space-3);
  background: var(--color-surface-alt, var(--color-surface));
  border-bottom: 1px solid var(--color-border);
}
.profile-card__name {
  font-weight: 600;
  font-size: 1.05rem;
  color: var(--color-ink);
}
.profile-card__id {
  font-family: var(--font-mono, monospace);
  font-size: 0.8rem;
  color: var(--color-muted);
}
.profile-card__fields {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.2rem var(--space-3);
  margin: 0;
  padding: 0 var(--space-3);
  font-size: 0.85rem;
}
.profile-card__fields dt {
  color: var(--color-muted);
  font-weight: 500;
}
.profile-card__fields dd {
  margin: 0;
  color: var(--color-ink);
}
.profile-card__skills {
  padding: 0 var(--space-3);
}
.profile-card__skills h4 {
  margin: 0 0 var(--space-2);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-muted);
}
.profile-card__skills ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.profile-card__skills li {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.25rem 0;
  border-bottom: 1px solid var(--color-border);
  font-size: 0.85rem;
}
.profile-card__skills li:last-child {
  border-bottom: 0;
}
.profile-card__skill-name {
  color: var(--color-ink);
}
.profile-card__skill-level {
  color: var(--color-muted);
  font-family: var(--font-mono, monospace);
  font-size: 0.78em;
}
.profile-card__narrative {
  padding: 0 var(--space-3) var(--space-3);
}
.profile-card__pre {
  margin: 0;
  font-family: var(--font-mono, monospace);
  font-size: 0.78rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: var(--color-ink);
  background: var(--color-surface-alt, var(--color-surface));
  padding: var(--space-2);
  border-radius: var(--radius-sm);
}
</style>
